'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { extractYouTubeVideoId } from '@up2stay/up2promo-commons';

interface VideoModuleDisplayProps {
  module: ProfileModuleDto;
}

type VideoModuleConfig = {
  type: 'video';
  url: string;
  title?: string;
  description?: string;
};

const DESCRIPTION_DISPLAY_MAX_LENGTH = 200;

function getVideoConfig(rawConfig: Record<string, unknown>): VideoModuleConfig {
  return {
    type: 'video',
    url: (rawConfig.url as string) || '',
    title: rawConfig.title as string | undefined,
    description: rawConfig.description as string | undefined,
  };
}

function capDescription(text: string): string {
  if (text.length <= DESCRIPTION_DISPLAY_MAX_LENGTH) return text;
  return `${text.slice(0, DESCRIPTION_DISPLAY_MAX_LENGTH)}…`;
}

/**
 * Displays a minimal YouTube embed (16:9). Only YouTube supported.
 */
export function VideoModuleDisplay({ module }: VideoModuleDisplayProps) {
  const config = getVideoConfig(module.config || {});

  if (!config.url) return null;

  const videoId = extractYouTubeVideoId(config.url);
  if (!videoId) return null;

  const embedUrl = `https://www.youtube.com/embed/${videoId}`;

  return (
    <div className="w-full min-w-0 max-w-full">
      {config.title?.trim() && (
        <h3 className="mb-2 break-words text-lg font-semibold [overflow-wrap:anywhere]">
          {config.title}
        </h3>
      )}
      <div className="relative aspect-video w-full max-w-full overflow-hidden rounded-lg">
        <iframe
          src={embedUrl}
          className="absolute inset-0 h-full w-full max-w-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={config.title || 'YouTube video'}
        />
      </div>
      {config.description?.trim() && (
        <p className="mt-2 break-words text-sm text-muted-foreground [overflow-wrap:anywhere]">
          {capDescription(config.description)}
        </p>
      )}
    </div>
  );
}
